import {
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { TruncateTooltipComponent } from './truncate-tooltip.component';

const TOOLTIP_POSITIONS: ConnectedPosition[] = [
  { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
  { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
];
const TOOLTIP_VIEWPORT_MARGIN = 8;

/**
 * Shows a tooltip with the full text of elements inside the host whose text is not fully visible
 * (cut with ellipsis or hidden). One listener on the host serves every matching element, including
 * ones rendered later.
 *
 * <div rnplTruncateTooltip=".text-ellipsis">…</div>
 * <div rnplTruncateTooltip=".btn" truncateTooltipTextSelector=".btn-title">…</div>
 */
@Directive({
  selector: '[rnplTruncateTooltip]'
})
export class TruncateTooltipDirective implements OnInit, OnDestroy {

  @Input('rnplTruncateTooltip') targetSelector: string;
  @Input() truncateTooltipTextSelector: string;

  private overlayRef: OverlayRef = null;
  private activeTarget: HTMLElement = null;
  private unlisteners: (() => void)[] = [];
  // last pointer position over the host, null when the pointer is outside it
  private pointer: { x: number, y: number } = null;
  private readonly onScroll = () => this.onScrollUnderPointer();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly overlay: Overlay,
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
    private readonly injector: Injector,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;

    // mouse events fire constantly while moving over the host, change detection is needed only to show the tooltip
    this.ngZone.runOutsideAngular(() => {
      this.unlisteners = [
        this.renderer.listen(host, 'mouseover', (event: MouseEvent) => this.onMouseOver(event)),
        this.renderer.listen(host, 'mousemove', (event: MouseEvent) => { this.pointer = { x: event.clientX, y: event.clientY }; }),
        this.renderer.listen(host, 'mouseout', (event: MouseEvent) => this.onMouseOut(event)),
      ];
      document.addEventListener('scroll', this.onScroll, true);
    });
  }

  ngOnDestroy(): void {
    this.unlisteners.forEach(unlisten => unlisten());
    document.removeEventListener('scroll', this.onScroll, true);
    this.hide();
  }

  private onMouseOver(event: MouseEvent): void {
    this.pointer = { x: event.clientX, y: event.clientY };
    this.updateForElement(event.target as HTMLElement);
  }

  private onMouseOut(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as Node;
    if (!relatedTarget || !this.elementRef.nativeElement.contains(relatedTarget)) {
      this.pointer = null;
    }

    if (!this.activeTarget) { return; }
    if (!relatedTarget || !this.activeTarget.contains(relatedTarget)) {
      this.hide();
    }
  }

  // content moves under a still pointer without mouse events: re-check what is under the pointer now
  private onScrollUnderPointer(): void {
    if (!this.pointer) { return; }

    const element = document.elementFromPoint(this.pointer.x, this.pointer.y) as HTMLElement;
    if (!element || !this.elementRef.nativeElement.contains(element)) {
      this.pointer = null;
      this.hide();
      return;
    }

    if (this.activeTarget && this.activeTarget.contains(element)) {
      this.overlayRef.updatePosition();
      return;
    }

    this.updateForElement(element);
  }

  private updateForElement(element: HTMLElement): void {
    const target = this.findTarget(element);
    if (target === this.activeTarget) { return; }

    this.hide();
    if (!target) { return; }

    const textElement = this.truncateTooltipTextSelector
      ? target.querySelector(this.truncateTooltipTextSelector) as HTMLElement
      : target;
    if (!textElement || !this.isTextCut(textElement)) { return; }

    const text = textElement.textContent.trim();
    if (!text) { return; }

    this.show(target, text);
  }

  private findTarget(element: HTMLElement): HTMLElement {
    if (!this.targetSelector || !element || !element.closest) { return null; }
    const target = element.closest(this.targetSelector) as HTMLElement;
    return target && this.elementRef.nativeElement.contains(target) ? target : null;
  }

  private isTextCut(element: HTMLElement): boolean {
    const hidden = !element.getClientRects().length;
    return hidden || element.scrollWidth > element.clientWidth;
  }

  private show(target: HTMLElement, text: string): void {
    this.activeTarget = target;

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(target)
      .withPositions(TOOLTIP_POSITIONS)
      .withFlexibleDimensions(false)
      .withViewportMargin(TOOLTIP_VIEWPORT_MARGIN)
      .withPush(true);

    // the overlay applies its position once the zone is stable
    this.ngZone.run(() => {
      this.overlayRef = this.overlay.create({ positionStrategy });

      // the module's resolver: the directive is used in lazy-loaded modules, the root resolver doesn't know the component
      const portal = new ComponentPortal(TruncateTooltipComponent, null, this.injector, this.componentFactoryResolver);
      const componentRef = this.overlayRef.attach(portal);
      componentRef.instance.text = text;
      componentRef.changeDetectorRef.detectChanges();
    });
  }

  private hide(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.activeTarget = null;
  }
}
